import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom, merge } from 'rxjs';

import { SessionStore } from '../../../core/auth/session.store';
import { TranslationService } from '../../../core/i18n/translation.service';
import { ConfirmationDialogComponent } from '../../../components/confirmation-dialog/confirmation-dialog.component';
import { ChecklistItemDefinitionMultiscaleComponent } from './checklist-item-definition-multiscale.component';
import { ChecklistItemDefinitionNumberComponent } from './checklist-item-definition-number.component';
import { ChecklistItemDefinitionNumberWithUnitComponent } from './checklist-item-definition-number-with-unit.component';
import { ChecklistItemDefinitionScaleComponent } from './checklist-item-definition-scale.component';
import { ChecklistItemDefinitionYesNoComponent } from './checklist-item-definition-yes-no.component';
import {
  CheckListItemType,
  CheckListStatus,
  ChecklistGroupDto,
  ChecklistDetailDto,
  ChecklistItemDto,
  CreateChecklistItemPayload,
  MultiScaleOptionPayload,
  NumberItemPayload,
  NumberWithUnitItemPayload,
  ScaleBucketPayload,
  ScaleItemPayload,
  YesNoItemPayload,
} from '../models/checklist.models';
import { ChecklistsApi } from '../services/checklists.api';

interface ItemTypeOption {
  value: CheckListItemType;
  labelKey: string;
  shortLabelKey: string;
}

interface ApiErrorBody {
  code?: string;
}

type PendingDeletion =
  | { kind: 'group'; group: ChecklistGroupDto }
  | { kind: 'item'; item: ChecklistItemDto; groupId: string };

const ITEM_TYPE_OPTIONS: ItemTypeOption[] = [
  {
    value: 'yes_no',
    labelKey: 'checklistCreator.itemType.yesNo',
    shortLabelKey: 'checklistCreator.itemTypeShort.yesNo',
  },
  {
    value: 'scale',
    labelKey: 'checklistCreator.itemType.scale',
    shortLabelKey: 'checklistCreator.itemTypeShort.scale',
  },
  {
    value: 'multiscale',
    labelKey: 'checklistCreator.itemType.multiscale',
    shortLabelKey: 'checklistCreator.itemTypeShort.multiscale',
  },
  {
    value: 'number',
    labelKey: 'checklistCreator.itemType.number',
    shortLabelKey: 'checklistCreator.itemTypeShort.number',
  },
  {
    value: 'number_with_unit',
    labelKey: 'checklistCreator.itemType.numberWithUnit',
    shortLabelKey: 'checklistCreator.itemTypeShort.numberWithUnit',
  },
];

@Component({
  selector: 'app-checklist-creator',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ConfirmationDialogComponent,
    ChecklistItemDefinitionYesNoComponent,
    ChecklistItemDefinitionScaleComponent,
    ChecklistItemDefinitionNumberComponent,
    ChecklistItemDefinitionNumberWithUnitComponent,
    ChecklistItemDefinitionMultiscaleComponent,
  ],
  templateUrl: './checklist-creator.component.html',
  styleUrl: './checklist-creator.component.css',
})
export class ChecklistCreatorComponent {
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ChecklistsApi);
  private readonly destroyRef = inject(DestroyRef);

  readonly isOpen = input(false);
  readonly sourceChecklistId = input<string | null>(null);
  readonly closeRequested = output<void>();
  readonly unsavedChangesChanged = output<boolean>();

  protected readonly i18n = inject(TranslationService);
  protected readonly sessionStore = inject(SessionStore);

  protected readonly itemTypeOptions = ITEM_TYPE_OPTIONS;
  protected readonly currentStep = signal(1);
  protected readonly isSubmittingStep = signal(false);
  protected readonly isSavingGroup = signal(false);
  protected readonly isSavingItem = signal(false);
  protected readonly isPublishingChecklist = signal(false);
  protected readonly isCloningChecklist = signal(false);
  protected readonly isDeletingEntity = signal(false);
  protected readonly editingGroupId = signal<string | null>(null);
  protected readonly editingItemId = signal<string | null>(null);
  protected readonly isAddingItem = signal(true);
  protected readonly editingItemOriginalGroupId = signal<string | null>(null);
  protected readonly currentChecklistId = signal<string | null>(null);
  protected readonly createdGroups = signal<ChecklistGroupDto[]>([]);
  protected readonly createdItems = signal<Record<string, ChecklistItemDto[]>>({});
  protected readonly stepError = signal('');
  protected readonly groupError = signal('');
  protected readonly itemError = signal('');
  protected readonly publishError = signal('');
  protected readonly publishRequirementsDialogOpen = signal(false);
  protected readonly publishSuccessDialogOpen = signal(false);
  protected readonly pendingDeletion = signal<PendingDeletion | null>(null);
  protected readonly isLoadingChecklist = signal(false);
  protected readonly isReadOnly = signal(false);
  protected readonly checklistCreated = computed(() => Boolean(this.currentChecklistId()));
  protected readonly canContinueToItems = computed(() => this.createdGroups().length > 0);
  protected readonly isEditingChecklist = computed(() => Boolean(this.sourceChecklistId()));

  protected readonly checklistForm = this.fb.nonNullable.group({
    title: ['', [Validators.required, Validators.maxLength(255)]],
    description: [''],
    version: [1, [Validators.required, Validators.min(1)]],
    status: this.fb.nonNullable.control<CheckListStatus>('draft', Validators.required),
  });

  protected readonly groupForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
  });

  protected readonly itemForm = this.fb.nonNullable.group({
    groupId: ['', Validators.required],
    title: ['', [Validators.required, Validators.maxLength(255)]],
    allowPhoto: false,
    maxScore: [1, [Validators.required, Validators.min(0)]],
    itemType: this.fb.nonNullable.control<CheckListItemType>('yes_no', Validators.required),
    yesValue: [1, Validators.required],
    noValue: [0, Validators.required],
    scaleMin: [0, Validators.required],
    scaleMax: [5, Validators.required],
    numberMin: [0, Validators.required],
    numberMax: [100, Validators.required],
    numberWithUnit: [''],
  });

  protected readonly scaleBuckets = new FormArray<FormGroup>([]);

  protected readonly numberWithUnitBuckets = this.fb.array([
    this.createScaleBucketGroup(0, 10, 10),
    this.createScaleBucketGroup(10.1, 20, 5),
  ]);

  protected readonly multiscaleOptions = this.fb.array([
    this.createMultiscaleOptionGroup('A', '', 5),
    this.createMultiscaleOptionGroup('B', '', 3),
  ]);

  protected readonly selectedItemType = toSignal(this.itemForm.controls.itemType.valueChanges, {
    initialValue: this.itemForm.controls.itemType.value,
  });
  protected readonly checklistStatus = toSignal(this.checklistForm.controls.status.valueChanges, {
    initialValue: this.checklistForm.controls.status.value,
  });
  protected readonly isChecklistActive = computed(() => this.checklistStatus() === 'active');
  protected readonly isEditingGroup = computed(() => Boolean(this.editingGroupId()));
  protected readonly isEditingItem = computed(() => Boolean(this.editingItemId()));
  protected readonly canPublishChecklist = computed(() => this.hasQuestionsInEveryGroup());
  protected readonly readOnlyBadgeLabel = computed(() => {
    return this.i18n.t(
      this.isChecklistActive()
        ? 'checklistCreator.badges.activeReadOnly'
        : 'checklistCreator.badges.readOnly',
    );
  });

  protected hasQuestionsInEveryGroup(): boolean {
    const groups = this.createdGroups();
    const itemsByGroup = this.createdItems();

    return groups.length > 0 && groups.every((group) => (itemsByGroup[group.id]?.length ?? 0) > 0);
  }
  protected readonly deleteConfirmationTitle = computed(() => {
    const pendingDeletion = this.pendingDeletion();
    if (!pendingDeletion) {
      return '';
    }

    return this.i18n.t(
      pendingDeletion.kind === 'group'
        ? 'checklistCreator.deleteConfirmation.groupTitle'
        : 'checklistCreator.deleteConfirmation.itemTitle',
    );
  });
  protected readonly deleteConfirmationMessage = computed(() => {
    const pendingDeletion = this.pendingDeletion();
    if (!pendingDeletion) {
      return '';
    }

    return this.i18n.t(
      pendingDeletion.kind === 'group'
        ? 'checklistCreator.deleteConfirmation.groupMessage'
        : 'checklistCreator.deleteConfirmation.itemMessage',
    );
  });
  protected readonly itemFormGroup = this.itemForm;
  protected readonly groupedItems = computed(() =>
    this.createdGroups().map((group) => ({
      group,
      items: this.createdItems()[group.id] ?? [],
    })),
  );
  protected readonly availableStep = computed(() => {
    if (this.canContinueToItems()) {
      return 3;
    }

    if (this.checklistCreated()) {
      return 2;
    }

    return 1;
  });

  constructor() {
    merge(
      this.checklistForm.valueChanges,
      this.groupForm.valueChanges,
      this.itemForm.valueChanges,
      this.scaleBuckets.valueChanges,
      this.numberWithUnitBuckets.valueChanges,
      this.multiscaleOptions.valueChanges,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.emitUnsavedChanges());

    effect(() => {
      if (!this.isOpen()) {
        return;
      }

      const checklistId = this.sourceChecklistId();
      if (checklistId) {
        void this.loadChecklist(checklistId);
        return;
      }

      this.resetCreator();
    });
  }

  protected saveChecklist(): Promise<void> {
    this.stepError.set('');
    this.checklistForm.markAllAsTouched();

    if (this.checklistForm.invalid || this.isSubmittingStep()) {
      return Promise.resolve();
    }

    if (this.isEditingChecklist()) {
      return this.updateChecklist();
    }

    return this.createChecklist();
  }

  private createChecklist(): Promise<void> {
    this.isSubmittingStep.set(true);
    const session = this.sessionStore.snapshot();

    return firstValueFrom(
      this.api.createChecklist({
        ...this.checklistForm.getRawValue(),
        created_by: session?.userCode ?? null,
      }),
    )
      .then((created) => {
        this.currentChecklistId.set(created.id);
        this.checklistForm.markAsPristine();
        this.emitUnsavedChanges();
        this.currentStep.set(2);
      })
      .catch((error: unknown) => {
        this.stepError.set(
          this.resolveChecklistSaveError(error, 'checklistCreator.errors.createChecklist'),
        );
      })
      .finally(() => {
        this.isSubmittingStep.set(false);
      });
  }

  private updateChecklist(): Promise<void> {
    const checklistId = this.currentChecklistId() ?? this.sourceChecklistId();

    if (!checklistId) {
      this.stepError.set(this.i18n.t('checklistCreator.errors.missingChecklist'));
      return Promise.resolve();
    }

    this.isSubmittingStep.set(true);

    return firstValueFrom(this.api.updateChecklist(checklistId, this.checklistForm.getRawValue()))
      .then((updated) => {
        this.currentChecklistId.set(updated.id);
        this.checklistForm.markAsPristine();
        this.emitUnsavedChanges();
        this.currentStep.set(2);
      })
      .catch((error: unknown) => {
        this.stepError.set(
          this.resolveChecklistSaveError(error, 'checklistCreator.errors.updateChecklist'),
        );
      })
      .finally(() => {
        this.isSubmittingStep.set(false);
      });
  }

  protected addGroup(): Promise<void> {
    this.groupError.set('');
    this.groupForm.markAllAsTouched();

    if (this.groupForm.invalid || this.isSavingGroup()) {
      return Promise.resolve();
    }

    if (this.editingGroupId()) {
      return this.updateGroup();
    }

    const checklistId = this.currentChecklistId();
    if (!checklistId) {
      this.groupError.set(this.i18n.t('checklistCreator.errors.missingChecklist'));
      return Promise.resolve();
    }

    this.isSavingGroup.set(true);

    return firstValueFrom(this.api.createGroup(checklistId, this.groupForm.getRawValue()))
      .then((createdGroup) => {
        this.createdGroups.update((groups) => [...groups, createdGroup]);
        this.createdItems.update((items) => ({ ...items, [createdGroup.id]: [] }));
        this.groupForm.reset({ name: '' });
        this.groupForm.markAsPristine();
        this.emitUnsavedChanges();

        if (!this.itemForm.controls.groupId.value) {
          this.itemForm.controls.groupId.setValue(createdGroup.id);
        }
      })
      .catch(() => {
        this.groupError.set(this.i18n.t('checklistCreator.errors.createGroup'));
      })
      .finally(() => {
        this.isSavingGroup.set(false);
      });
  }

  protected editGroup(group: ChecklistGroupDto): void {
    if (this.isReadOnly() || this.isSavingGroup()) {
      return;
    }

    this.groupError.set('');
    this.editingGroupId.set(group.id);
    this.groupForm.reset({ name: group.name });
    this.groupForm.markAsPristine();
    this.emitUnsavedChanges();
  }

  protected cancelGroupEdit(): void {
    if (this.isSavingGroup()) {
      return;
    }

    this.editingGroupId.set(null);
    this.groupForm.reset({ name: '' });
    this.groupForm.markAsPristine();
    this.groupError.set('');
    this.emitUnsavedChanges();
  }

  private updateGroup(): Promise<void> {
    const groupId = this.editingGroupId();
    if (!groupId) {
      return Promise.resolve();
    }

    this.isSavingGroup.set(true);

    return firstValueFrom(this.api.updateGroup(groupId, this.groupForm.getRawValue()))
      .then((updatedGroup) => {
        this.createdGroups.update((groups) =>
          groups.map((group) => (group.id === updatedGroup.id ? updatedGroup : group)),
        );
        this.editingGroupId.set(null);
        this.groupForm.reset({ name: '' });
        this.groupForm.markAsPristine();
        this.emitUnsavedChanges();
      })
      .catch(() => {
        this.groupError.set(this.i18n.t('checklistCreator.errors.updateGroup'));
      })
      .finally(() => {
        this.isSavingGroup.set(false);
      });
  }

  protected addItem(): Promise<void> {
    this.itemError.set('');
    this.itemForm.markAllAsTouched();
    this.scaleBuckets.markAllAsTouched();
    this.numberWithUnitBuckets.markAllAsTouched();
    this.multiscaleOptions.markAllAsTouched();

    if (this.itemForm.invalid || this.isSavingItem()) {
      return Promise.resolve();
    }

    const payload = this.buildItemPayload();
    if (!payload) {
      this.itemError.set(this.i18n.t('checklistCreator.errors.invalidItemDefinition'));
      return Promise.resolve();
    }

    if (this.editingItemId()) {
      return this.updateItem(payload);
    }

    this.isSavingItem.set(true);

    return firstValueFrom(this.api.createItem(this.itemForm.controls.groupId.value, payload))
      .then((createdItem) => {
        const groupId = this.itemForm.controls.groupId.value;
        this.createdItems.update((itemsByGroup) => ({
          ...itemsByGroup,
          [groupId]: [...(itemsByGroup[groupId] ?? []), createdItem],
        }));
        this.resetItemForm();
        this.emitUnsavedChanges();
      })
      .catch(() => {
        this.itemError.set(this.i18n.t('checklistCreator.errors.createItem'));
      })
      .finally(() => {
        this.isSavingItem.set(false);
      });
  }

  protected editItem(groupId: string, item: ChecklistItemDto): void {
    if (this.isReadOnly() || this.isSavingItem() || this.isDeletingEntity() || this.editingItemId() === item.id) {
      return;
    }

    this.itemError.set('');
    this.isAddingItem.set(false);
    this.editingItemId.set(item.id);
    this.editingItemOriginalGroupId.set(groupId);
    this.resetItemForm();
    this.applyItemToForm(groupId, item);
    this.itemForm.markAsPristine();
    this.scaleBuckets.markAsPristine();
    this.numberWithUnitBuckets.markAsPristine();
    this.multiscaleOptions.markAsPristine();
    this.emitUnsavedChanges();
  }

  protected cancelItemEdit(): void {
    if (this.isSavingItem()) {
      return;
    }

    this.editingItemId.set(null);
    this.editingItemOriginalGroupId.set(null);
    this.resetItemForm();
    this.itemError.set('');
    this.emitUnsavedChanges();
  }

  protected openNewItem(): void {
    if (this.isReadOnly() || this.isSavingItem() || this.isDeletingEntity()) {
      return;
    }

    this.cancelItemEdit();
    this.isAddingItem.set(true);
  }

  private updateItem(payload: CreateChecklistItemPayload): Promise<void> {
    const itemId = this.editingItemId();
    const originalGroupId = this.editingItemOriginalGroupId();
    const nextGroupId = this.itemForm.controls.groupId.value;

    if (!itemId || !originalGroupId) {
      return Promise.resolve();
    }

    this.isSavingItem.set(true);

    return firstValueFrom(this.api.updateItem(itemId, { ...payload, group_id: nextGroupId }))
      .then((updatedItem) => {
        this.createdItems.update((itemsByGroup) => {
          if (originalGroupId === nextGroupId) {
            return {
              ...itemsByGroup,
              [originalGroupId]: (itemsByGroup[originalGroupId] ?? []).map(
                (candidate) => candidate.id === updatedItem.id ? updatedItem : candidate,
              ),
            };
          }
          const nextItemsByGroup = { ...itemsByGroup };
          nextItemsByGroup[originalGroupId] = (nextItemsByGroup[originalGroupId] ?? []).filter(
            (candidate) => candidate.id !== updatedItem.id,
          );
          nextItemsByGroup[nextGroupId] = [
            ...(nextItemsByGroup[nextGroupId] ?? []).filter(
              (candidate) => candidate.id !== updatedItem.id,
            ),
            updatedItem,
          ];
          return nextItemsByGroup;
        });
        this.editingItemId.set(null);
        this.editingItemOriginalGroupId.set(null);
        this.resetItemForm();
        this.emitUnsavedChanges();
      })
      .catch(() => {
        this.itemError.set(this.i18n.t('checklistCreator.errors.updateItem'));
      })
      .finally(() => {
        this.isSavingItem.set(false);
      });
  }

  protected publishChecklist(): Promise<void> {
    const checklistId = this.currentChecklistId();

    this.publishError.set('');

    if (!checklistId || this.isPublishingChecklist() || this.isReadOnly()) {
      return Promise.resolve();
    }

    if (!this.canPublishChecklist()) {
      this.publishRequirementsDialogOpen.set(true);
      return Promise.resolve();
    }

    this.isPublishingChecklist.set(true);

    return firstValueFrom(this.api.updateChecklist(checklistId, { status: 'active' }))
      .then((updated) => {
        this.checklistForm.controls.status.setValue(updated.status);
        this.checklistForm.markAsPristine();
        this.setReadOnlyMode(false);
        this.publishSuccessDialogOpen.set(true);
        this.emitUnsavedChanges();
      })
      .catch(() => {
        this.publishError.set(this.i18n.t('checklistCreator.errors.publishChecklist'));
      })
      .finally(() => {
        this.isPublishingChecklist.set(false);
      });
  }

  protected cloneChecklist(): Promise<void> {
    const checklistId = this.currentChecklistId();

    this.stepError.set('');

    if (!checklistId || this.isCloningChecklist()) {
      return Promise.resolve();
    }

    this.currentStep.set(1);
    this.isCloningChecklist.set(true);

    return firstValueFrom(this.api.cloneChecklist(checklistId))
      .then((clonedChecklist) => {
        this.applyChecklist(clonedChecklist);
        this.currentStep.set(1);
      })
      .catch((error: unknown) => {
        this.stepError.set(
          this.resolveChecklistSaveError(error, 'checklistCreator.errors.cloneChecklist'),
        );
      })
      .finally(() => {
        this.isCloningChecklist.set(false);
      });
  }

  protected requestGroupDeletion(group: ChecklistGroupDto): void {
    if (this.isReadOnly() || this.isDeletingEntity()) {
      return;
    }

    this.groupError.set('');
    this.pendingDeletion.set({ kind: 'group', group });
  }

  protected requestItemDeletion(groupId: string, item: ChecklistItemDto): void {
    if (this.isReadOnly() || this.isDeletingEntity()) {
      return;
    }

    this.itemError.set('');
    this.pendingDeletion.set({ kind: 'item', groupId, item });
  }

  protected getGroupItemsCount(groupId: string): number {
    return this.createdItems()[groupId]?.length ?? 0;
  }

  protected getItemTypeShortLabel(itemType: CheckListItemType): string {
    const option = ITEM_TYPE_OPTIONS.find((candidate) => candidate.value === itemType);

    return option ? this.i18n.t(option.shortLabelKey) : itemType;
  }

  protected getItemTypeBadgeClass(itemType: CheckListItemType): string {
    return `checklist-creator__item-type-badge--${itemType.replaceAll('_', '-')}`;
  }

  protected cancelDeletion(): void {
    if (this.isDeletingEntity()) {
      return;
    }

    this.pendingDeletion.set(null);
  }

  protected confirmDeletion(): Promise<void> {
    const pendingDeletion = this.pendingDeletion();
    if (!pendingDeletion || this.isDeletingEntity()) {
      return Promise.resolve();
    }

    this.isDeletingEntity.set(true);

    const deletion =
      pendingDeletion.kind === 'group'
        ? this.deleteGroup(pendingDeletion.group)
        : this.deleteItem(pendingDeletion.groupId, pendingDeletion.item);

    return deletion.finally(() => {
      this.isDeletingEntity.set(false);
    });
  }

  protected closePublishRequirementsDialog(): void {
    this.publishRequirementsDialogOpen.set(false);
  }

  protected closePublishSuccessDialog(): void {
    this.publishSuccessDialogOpen.set(false);
  }

  protected goToItemsStep(): void {
    if (this.canContinueToItems()) {
      this.currentStep.set(3);
    }
  }

  protected goToStep(step: number): void {
    if (step >= 1 && step <= this.availableStep()) {
      this.currentStep.set(step);
    }
  }

  protected close(): void {
    this.closeRequested.emit();
  }

  private deleteGroup(group: ChecklistGroupDto): Promise<void> {
    return firstValueFrom(this.api.deleteGroup(group.id))
      .then(() => {
        const remainingGroups = this.createdGroups().filter((candidate) => candidate.id !== group.id);

        this.createdGroups.set(remainingGroups);
        this.createdItems.update((itemsByGroup) => {
          const nextItemsByGroup = { ...itemsByGroup };
          delete nextItemsByGroup[group.id];
          return nextItemsByGroup;
        });

        if (this.itemForm.controls.groupId.value === group.id) {
          this.itemForm.controls.groupId.setValue(remainingGroups[0]?.id ?? '');
        }

        if (this.editingGroupId() === group.id) {
          this.editingGroupId.set(null);
          this.groupForm.reset({ name: '' });
          this.groupForm.markAsPristine();
        }

        if (this.editingItemOriginalGroupId() === group.id) {
          this.editingItemId.set(null);
          this.editingItemOriginalGroupId.set(null);
          this.resetItemForm();
        }

        this.pendingDeletion.set(null);
      })
      .catch(() => {
        this.groupError.set(this.i18n.t('checklistCreator.errors.deleteGroup'));
      });
  }

  private deleteItem(groupId: string, item: ChecklistItemDto): Promise<void> {
    return firstValueFrom(this.api.deleteItem(item.id))
      .then(() => {
        this.createdItems.update((itemsByGroup) => ({
          ...itemsByGroup,
          [groupId]: (itemsByGroup[groupId] ?? []).filter((candidate) => candidate.id !== item.id),
        }));

        if (this.editingItemId() === item.id) {
          this.editingItemId.set(null);
          this.editingItemOriginalGroupId.set(null);
          this.resetItemForm();
        }

        this.pendingDeletion.set(null);
      })
      .catch(() => {
        this.itemError.set(this.i18n.t('checklistCreator.errors.deleteItem'));
      });
  }

  protected addScaleBucket(): void {
    this.scaleBuckets.push(this.createScaleBucketGroup(0, 0, 0));
  }

  protected removeScaleBucket(index: number): void {
    this.scaleBuckets.removeAt(index);
  }

  protected addNumberWithUnitBucket(): void {
    this.numberWithUnitBuckets.push(this.createScaleBucketGroup(0, 0, 0));
  }

  protected removeNumberWithUnitBucket(index: number): void {
    if (this.numberWithUnitBuckets.length > 1) {
      this.numberWithUnitBuckets.removeAt(index);
    }
  }

  protected addMultiscaleOption(): void {
    const nextLabel = String.fromCharCode(65 + this.multiscaleOptions.length);
    this.multiscaleOptions.push(this.createMultiscaleOptionGroup(nextLabel, '', 0));
  }

  protected removeMultiscaleOption(index: number): void {
    if (this.multiscaleOptions.length > 1) {
      this.multiscaleOptions.removeAt(index);
    }
  }

  protected readonly addScaleBucketHandler = () => this.addScaleBucket();
  protected readonly removeScaleBucketHandler = (index: number) => this.removeScaleBucket(index);
  protected readonly addNumberWithUnitBucketHandler = () => this.addNumberWithUnitBucket();
  protected readonly removeNumberWithUnitBucketHandler = (index: number) => this.removeNumberWithUnitBucket(index);
  protected readonly addMultiscaleOptionHandler = () => this.addMultiscaleOption();
  protected readonly removeMultiscaleOptionHandler = (index: number) => this.removeMultiscaleOption(index);

  private setReadOnlyMode(isReadOnly: boolean): void {
    this.isReadOnly.set(isReadOnly);

    const action = isReadOnly ? 'disable' : 'enable';
    this.checklistForm[action]({ emitEvent: false });
    this.groupForm[action]({ emitEvent: false });
    this.itemForm[action]({ emitEvent: false });
    this.scaleBuckets[action]({ emitEvent: false });
    this.numberWithUnitBuckets[action]({ emitEvent: false });
    this.multiscaleOptions[action]({ emitEvent: false });
    this.emitUnsavedChanges();
  }

  private emitUnsavedChanges(): void {
    this.unsavedChangesChanged.emit(!this.isReadOnly() && this.hasUnsavedChanges());
  }

  private hasUnsavedChanges(): boolean {
    return (
      this.checklistForm.dirty ||
      this.groupForm.dirty ||
      this.itemForm.dirty ||
      this.scaleBuckets.dirty ||
      this.numberWithUnitBuckets.dirty ||
      this.multiscaleOptions.dirty
    );
  }

  private resetItemForm(): void {
    const groupId = this.itemForm.controls.groupId.value;
    this.itemForm.reset({
      groupId,
      title: '',
      allowPhoto: false,
      maxScore: 1,
      itemType: 'yes_no',
      yesValue: 1,
      noValue: 0,
      scaleMin: 0,
      scaleMax: 5,
      numberMin: 0,
      numberMax: 100,
      numberWithUnit: '',
    });

    this.scaleBuckets.clear();

    this.numberWithUnitBuckets.clear();
    this.numberWithUnitBuckets.push(this.createScaleBucketGroup(0, 10, 10));
    this.numberWithUnitBuckets.push(this.createScaleBucketGroup(10.1, 20, 5));

    this.multiscaleOptions.clear();
    this.multiscaleOptions.push(this.createMultiscaleOptionGroup('A', '', 5));
    this.multiscaleOptions.push(this.createMultiscaleOptionGroup('B', '', 3));
    this.itemForm.markAsPristine();
    this.scaleBuckets.markAsPristine();
    this.numberWithUnitBuckets.markAsPristine();
    this.multiscaleOptions.markAsPristine();
  }

  private resolveChecklistSaveError(error: unknown, fallbackKey: string): string {
    if (
      error instanceof HttpErrorResponse &&
      this.isApiErrorBody(error.error) &&
      error.error.code === 'checklist_duplicate_title_version'
    ) {
      return this.i18n.t('checklistCreator.errors.duplicateTitleVersion');
    }

    return this.i18n.t(fallbackKey);
  }

  private isApiErrorBody(value: unknown): value is ApiErrorBody {
    return typeof value === 'object' && value !== null && 'code' in value;
  }

  private resetCreator(): void {
    this.isAddingItem.set(true);
    this.setReadOnlyMode(false);
    this.currentStep.set(1);
    this.stepError.set('');
    this.groupError.set('');
    this.itemError.set('');
    this.publishError.set('');
    this.publishRequirementsDialogOpen.set(false);
    this.publishSuccessDialogOpen.set(false);
    this.pendingDeletion.set(null);
    this.editingGroupId.set(null);
    this.editingItemId.set(null);
    this.editingItemOriginalGroupId.set(null);
    this.currentChecklistId.set(null);
    this.createdGroups.set([]);
    this.createdItems.set({});

    this.checklistForm.reset({
      title: '',
      description: '',
      version: 1,
      status: 'draft',
    });

    this.groupForm.reset({ name: '' });
    this.resetItemForm();
    this.checklistForm.markAsPristine();
    this.groupForm.markAsPristine();
    this.emitUnsavedChanges();
  }

  private async loadChecklist(checklistId: string): Promise<void> {
    this.isLoadingChecklist.set(true);
    this.stepError.set('');
    this.groupError.set('');
    this.itemError.set('');
    this.publishError.set('');
    this.publishRequirementsDialogOpen.set(false);
    this.publishSuccessDialogOpen.set(false);
    this.pendingDeletion.set(null);
    this.editingGroupId.set(null);
    this.editingItemId.set(null);
    this.editingItemOriginalGroupId.set(null);

    try {
      const checklist = await firstValueFrom(this.api.getChecklist(checklistId));
      this.applyChecklist(checklist);
    } catch {
      this.stepError.set(this.i18n.t('checklistCreator.errors.loadChecklist'));
    } finally {
      this.isLoadingChecklist.set(false);
    }
  }

  private applyChecklist(checklist: ChecklistDetailDto): void {
    this.isAddingItem.set(true);
    this.setReadOnlyMode(false);
    this.currentChecklistId.set(checklist.id);
    this.currentStep.set(1);

    this.checklistForm.reset({
      title: checklist.title,
      description: checklist.description ?? '',
      version: checklist.version,
      status: checklist.status,
    });

    const groups = checklist.groups ?? [];
    this.createdGroups.set(groups.map((group) => ({
      id: group.id,
      name: group.name,
      checklist_id: group.checklist_id,
    })));
    this.createdItems.set(
      Object.fromEntries(groups.map((group) => [group.id, group.items ?? []])),
    );

    const firstGroupId = groups[0]?.id ?? '';
    const firstItem = groups[0]?.items?.[0] ?? null;
    this.groupForm.reset({ name: '' });

    this.resetItemForm();
    this.itemForm.controls.groupId.setValue(firstGroupId);

    if (firstItem && checklist.status !== 'draft') {
      this.applyItemToForm(firstGroupId, firstItem);
    }

    this.setReadOnlyMode(checklist.status !== 'draft');
    this.checklistForm.markAsPristine();
    this.groupForm.markAsPristine();
    this.itemForm.markAsPristine();
    this.scaleBuckets.markAsPristine();
    this.numberWithUnitBuckets.markAsPristine();
    this.multiscaleOptions.markAsPristine();
    this.emitUnsavedChanges();
  }

  private buildItemPayload(): CreateChecklistItemPayload | null {
    const raw = this.itemForm.getRawValue();

    const base = {
      title: raw.title,
      allow_photo: raw.allowPhoto,
      max_score: Number(raw.maxScore),
      item_type: raw.itemType,
    } as const;

    if (raw.itemType === 'yes_no') {
      return {
        ...base,
        item: {
          Yes: { value: Number(raw.yesValue) },
          No: { value: Number(raw.noValue) },
        },
      };
    }

    if (raw.itemType === 'scale') {
      return {
        ...base,
        item: {
          min: Number(raw.scaleMin),
          max: Number(raw.scaleMax),
          scaleBucket: this.readBuckets(this.scaleBuckets),
        },
      };
    }

    if (raw.itemType === 'number') {
      return {
        ...base,
        item: {
          min: Number(raw.numberMin),
          max: Number(raw.numberMax),
        },
      };
    }

    if (raw.itemType === 'number_with_unit') {
      return {
        ...base,
        item: {
          Unit: raw.numberWithUnit,
          scaleBucket: this.readBuckets(this.numberWithUnitBuckets),
        },
      };
    }

    if (raw.itemType === 'multiscale') {
      return {
        ...base,
        item: this.multiscaleOptions.getRawValue().map((option) => ({
          optionID: option.optionId,
          title: option.title,
          score: Number(option.score),
        })),
      };
    }

    return null;
  }

  private asScaleItemPayload(value: ChecklistItemDto['item']): ScaleItemPayload {
    return value as ScaleItemPayload;
  }

  private asNumberItemPayload(value: ChecklistItemDto['item']): NumberItemPayload {
    return value as NumberItemPayload;
  }

  private asNumberWithUnitItemPayload(value: ChecklistItemDto['item']): NumberWithUnitItemPayload {
    return value as NumberWithUnitItemPayload;
  }

  private asYesNoItemPayload(value: ChecklistItemDto['item']): YesNoItemPayload {
    return value as YesNoItemPayload;
  }

  private asMultiScaleOptionsPayload(value: ChecklistItemDto['item']): MultiScaleOptionPayload[] {
    return value as MultiScaleOptionPayload[];
  }

  private applyItemToForm(groupId: string, item: ChecklistItemDto): void {
    this.itemForm.patchValue({
      groupId,
      title: item.title,
      allowPhoto: item.allow_photo,
      maxScore: item.max_score,
      itemType: item.item_type,
    });

    this.scaleBuckets.clear();
    this.numberWithUnitBuckets.clear();
    this.multiscaleOptions.clear();

    if (item.item_type === 'yes_no') {
      const value = this.asYesNoItemPayload(item.item);
      this.itemForm.patchValue({
        yesValue: value.Yes.value,
        noValue: value.No.value,
      });
      return;
    }

    if (item.item_type === 'scale') {
      const value = this.asScaleItemPayload(item.item);
      this.itemForm.patchValue({
        scaleMin: value.min,
        scaleMax: value.max,
      });
      for (const bucket of value.scaleBucket) {
        this.scaleBuckets.push(
          this.createScaleBucketGroup(bucket.MinValue, bucket.MaxValue, bucket.Score),
        );
      }
      return;
    }

    if (item.item_type === 'number') {
      const value = this.asNumberItemPayload(item.item);
      this.itemForm.patchValue({
        numberMin: value.min,
        numberMax: value.max,
      });
      return;
    }

    if (item.item_type === 'number_with_unit') {
      const value = this.asNumberWithUnitItemPayload(item.item);
      this.itemForm.patchValue({
        numberWithUnit: value.Unit,
      });
      for (const bucket of value.scaleBucket) {
        this.numberWithUnitBuckets.push(
          this.createScaleBucketGroup(bucket.MinValue, bucket.MaxValue, bucket.Score),
        );
      }
      return;
    }

    const options = this.asMultiScaleOptionsPayload(item.item);
    for (const option of options) {
      this.multiscaleOptions.push(
        this.createMultiscaleOptionGroup(option.optionID, option.title, option.score),
      );
    }
  }

  private readBuckets(formArray: FormArray): ScaleBucketPayload[] {
    return formArray.getRawValue().map((bucket) => ({
      MinValue: Number(bucket.minValue),
      MaxValue: Number(bucket.maxValue),
      Score: Number(bucket.score),
    }));
  }

  private createScaleBucketGroup(minValue: number, maxValue: number, score: number) {
    return this.fb.nonNullable.group({
      minValue: [minValue, Validators.required],
      maxValue: [maxValue, Validators.required],
      score: [score, [Validators.required, Validators.min(0)]],
    });
  }

  private createMultiscaleOptionGroup(optionId: string, title: string, score: number) {
    return this.fb.nonNullable.group({
      optionId: [optionId, Validators.required],
      title: [title, Validators.required],
      score: [score, [Validators.required, Validators.min(0)]],
    });
  }
}
